import ErrorIcon from '@mui/icons-material/Error'
import { Button, Box, Typography } from '@mui/material'
import { deleteUser } from 'api/management'
import { OPERATION_FLOW_TYPES } from 'config'
import { useAlert, useConfirm } from 'hooks/useDialog'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { User, SubGroup } from 'types'
import { isGroupUserAmountEnough } from 'utils/businessRules'

import { useLoaderData } from './UsersConfig.loader'

export interface DeleteUserButtonProps {
  user: User
}

export default function DeleteUserButton({ user }: DeleteUserButtonProps) {
  const {
    reset,
    formState: { isDirty },
  } = useFormContext()
  const confirm = useConfirm()
  const { users, subGroups } = useLoaderData()
  const alert = useAlert()
  const navigate = useNavigate()

  const userSubGroups = useMemo(() => {
    const names = new Set(user.subGroups)
    return subGroups.filter((x) => names.has(x.name))
  }, [subGroups, user.subGroups])

  return (
    <Button
      variant="contained"
      color="secondary"
      onClick={() => {
        confirm({
          message: <ConfirmMessage user={user} users={users} subGroups={userSubGroups} />,
          actionText: '削除',
          cancelText: 'キャンセル',
          actionProps: {
            color: 'secondary',
          },
          onConfirm: async () => {
            try {
              if (isDirty) {
                reset()
              }
              if (user.isMainUser) {
                alert({
                  message:
                    '主要ユーザーのため削除できません\n削除したい場合は、サポート窓口へお問い合わせください',
                })
                return
              }
              await deleteUser(user.userID)
              alert({ message: 'ユーザーが削除されました' })
              navigate('..', { replace: true })
            } catch {
              alert({ message: 'ユーザー削除が失敗しました' })
            }
          },
        })
      }}
      data-testid="delete-button"
    >
      削除
    </Button>
  )
}

interface ConfirmMessageProps {
  user: User
  users: User[]
  subGroups: SubGroup[]
}
function ConfirmMessage({ user, users, subGroups }: ConfirmMessageProps) {
  return (
    <Box textAlign="left">
      <Typography variant="body1">
        このユーザーを削除しますか？削除を行うとこの処理は取り消せません
      </Typography>

      <Box padding={2} marginBottom={1}>
        <Typography variant="body1" color="textSecondary">
          {user.name}
        </Typography>
        <Box>
          <Typography variant="body1" color="textSecondary">
            email: {user.email}
          </Typography>
        </Box>
      </Box>

      <Typography variant="body1">以下のグループの担当者からも削除されます</Typography>
      <GroupList user={user} users={users} subGroups={subGroups} />
    </Box>
  )
}

interface GroupListProps {
  user: User
  users: User[]
  subGroups: SubGroup[]
}
function GroupList({ subGroups, users, user }: GroupListProps) {
  let someWarn = false
  const rows = subGroups.map(({ name, operationFlowType }) => {
    const remainUsers = users
      .filter((x) => x.subGroups.includes(name))
      .filter((x) => x.email !== user.email)

    const warn = !isGroupUserAmountEnough({
      operationFlowType,
      users: remainUsers,
    })
    if (warn) {
      someWarn = true
    }

    return (
      <Box key={name} display="flex" alignItems="center">
        <Typography variant="body2" color="textSecondary">
          {name}
        </Typography>
        <Box marginLeft={2}>
          <Box display="flex" alignItems="center">
            <Typography variant="body2" color="textSecondary">
              (運用フロー： {OPERATION_FLOW_TYPES[operationFlowType]})
            </Typography>
            {warn && (
              <Box marginLeft={1} display="flex">
                <ErrorIcon color="error" />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    )
  })

  return (
    <Box padding={2}>
      {rows}
      {someWarn && (
        <Box marginTop={1}>
          <Typography variant="body2" color="error">
            所属グループで必要な権限をもったユーザーが不足する可能性があります
          </Typography>
        </Box>
      )}
    </Box>
  )
}
